import type { MaxPlanKey, MaxStoreConfig } from './index';

/** Cycle length when a plan does not say: the key is the term. */
const DEFAULT_MONTHS: Record<MaxPlanKey, number> = { monthly: 1, yearly: 12 };

export const getMaxPlanMonths = (
  cfg: MaxStoreConfig,
  key: MaxPlanKey,
): number => cfg.plans[key].months ?? DEFAULT_MONTHS[key];

/**
 * Everything a surface needs to describe a plan's term. Surfaces pick copy by
 * these keys and never by `MaxPlanKey`, which is a Shopify-mapping name, not a
 * term: IN's `monthly` plan is a 3-month one-time purchase.
 */
export interface MaxPlanTerms {
  key: MaxPlanKey;
  months: number;
  /** Bought once, never billed — no suffix, no per-month figures. */
  oneTime: boolean;
  /** "Monthly" | "{count} months" | "1 year" — a 12-month plan is always "1 year". */
  labelKey: 'monthly' | 'months' | 'year';
  /** "/mo" | "/yr" | "/{count} mo" | nothing */
  suffixKey: 'perMonth' | 'perYear' | 'perMonths' | 'none';
  /** Cart bullet: "Billed monthly…" | "Billed yearly…" | "One-time payment." */
  billingKey: 'monthly' | 'yearly' | 'oneTime';
  /** Cycle price spread over its months (the "That's just …/mo" figure). */
  perMonthEquivalent: number;
}

export const getMaxPlanTerms = (
  cfg: MaxStoreConfig,
  key: MaxPlanKey,
): MaxPlanTerms => {
  const months = getMaxPlanMonths(cfg, key);
  const price = cfg.plans[key].price;
  const perMonthEquivalent = price / months;
  if (cfg.oneTime) {
    return {
      key,
      months,
      oneTime: true,
      labelKey: months === 12 ? 'year' : 'months',
      suffixKey: 'none',
      billingKey: 'oneTime',
      perMonthEquivalent,
    };
  }
  const common = { key, months, oneTime: false, perMonthEquivalent } as const;
  if (months === 1) {
    return { ...common, labelKey: 'monthly', suffixKey: 'perMonth', billingKey: 'monthly' };
  }
  if (months === 12) {
    return { ...common, labelKey: 'year', suffixKey: 'perYear', billingKey: 'yearly' };
  }
  // A subscription billed every N months has no bullet copy of its own yet.
  return { ...common, labelKey: 'months', suffixKey: 'perMonths', billingKey: 'monthly' };
};

/** A year of the short plan: 12 × monthly, or 4 × a 3-month plan. */
const shortPlanYear = (cfg: MaxStoreConfig): number =>
  cfg.plans.monthly.price * (12 / getMaxPlanMonths(cfg, 'monthly'));

/** Yearly saving against a year of the short plan, unless the store overrides it. */
export const getMaxYearlySavePercent = (cfg: MaxStoreConfig): number => {
  if (cfg.savePercent !== undefined) return cfg.savePercent;
  return Math.round((1 - cfg.plans.yearly.price / shortPlanYear(cfg)) * 100);
};

export type MaxYearlySaving =
  | { kind: 'percent'; percent: number }
  | { kind: 'amount'; amount: number };

/** What the yearly badge says: a percentage for subscriptions, rupees-off for one-time stores. */
export const getMaxYearlySaving = (cfg: MaxStoreConfig): MaxYearlySaving =>
  cfg.oneTime
    ? { kind: 'amount', amount: shortPlanYear(cfg) - cfg.plans.yearly.price }
    : { kind: 'percent', percent: getMaxYearlySavePercent(cfg) };
